import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { EASE } from "@/animations/motion";
import { ArrowRight } from "@/icons";
import { IndustryDashboard } from "@/components/detail/IndustryDashboard";
import type { IndustryDetail } from "@/types/industryDetail";

/** Interactive industry selector — pick a sector to preview its dashboard + summary. */
export function IndustrySelector({ industries }: { industries: IndustryDetail[] }) {
  const [active, setActive] = useState(0);
  const current = industries[active];

  return (
    <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
      <div>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Industries">
          {industries.map((ind, i) => (
            <button
              key={ind.slug}
              role="tab"
              aria-selected={active === i}
              data-testid={`industry-selector-${ind.slug}`}
              onClick={() => setActive(i)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300",
                active === i
                  ? "border-transparent text-white"
                  : "border-border bg-card text-foreground hover:border-primary/30",
              )}
              style={active === i ? { background: ind.accent } : undefined}
            >
              <ind.icon className="h-4 w-4" />
              {ind.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.slug}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="mt-8"
          >
            <h3 className="font-heading text-2xl font-bold text-foreground">{current.name}</h3>
            <p className="mt-3 text-muted-foreground">{current.tagline}</p>
            <Link
              to={`/industries/${current.slug}`}
              data-testid={`industry-selector-link-${current.slug}`}
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5"
            >
              Explore {current.name} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.slug}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.35, ease: EASE }}
        >
          <IndustryDashboard name={current.name} accent={current.accent} kpis={current.outcomes} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
